import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

const MarkdownRenderer = ({ children, variant = 'default', className = '', darkMode = false }) => {
  const baseClasses = "prose dark:prose-invert max-w-none";

  const components = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={darkMode ? oneDark : oneLight}
          language={match[1]}
          PreTag="div"
          className="rounded-xl shadow-lg !my-4"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded-md text-sm font-mono" {...props}>
          {children}
        </code>
      );
    },
    table({ children }) {
      return (
        <div className="overflow-x-auto my-6 rounded-xl shadow-lg">
          <table className="min-w-full border-collapse">
            {children}
          </table>
        </div>
      );
    },
    th({ children }) {
      return (
        <th className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 text-left font-semibold first:rounded-tl-xl last:rounded-tr-xl">
          {children}
        </th>
      );
    },
    td({ children }) {
      return (
        <td className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          {children}
        </td>
      );
    },
    ul({ children }) {
      return (
        <div className="my-5 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border-l-4 border-blue-500 shadow-sm">
          <ul className="list-none space-y-3">{children}</ul>
        </div>
      );
    },
    ol({ children }) {
      return (
        <div className="my-5 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl border-l-4 border-emerald-500 shadow-sm">
          <ol className="list-none space-y-3 counter-reset-item">{children}</ol>
        </div>
      );
    },
    li({ children, index, ordered }) {
      return (
        <li className="flex items-start gap-3 leading-relaxed">
          {ordered ? (
            <span className="flex-shrink-0 w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-sm font-bold rounded-full flex items-center justify-center shadow-md">
              {(index || 0) + 1}
            </span>
          ) : (
            <span className="flex-shrink-0 mt-1.5">
              <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </span>
          )}
          <span className="flex-1 text-gray-700 dark:text-gray-300">{children}</span>
        </li>
      );
    },
    p({ children }) {
      return <p className="leading-relaxed mb-4 text-gray-700 dark:text-gray-300">{children}</p>;
    },
    h1({ children }) {
      return (
        <div className="mb-6 mt-2">
          <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 pb-3 border-b-2 border-gradient-to-r from-blue-200 to-indigo-200 dark:border-gray-700 flex items-center gap-3">
            <span className="text-3xl">📊</span>
            {children}
          </h1>
        </div>
      );
    },
    h2({ children }) {
      return (
        <div className="mb-4 mt-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 pb-2 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <span className="w-1.5 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"></span>
            {children}
          </h2>
        </div>
      );
    },
    h3({ children }) {
      return (
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-3 mt-5 flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          {children}
        </h3>
      );
    },
    blockquote({ children }) {
      return (
        <blockquote className="my-5 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-l-4 border-amber-500 rounded-r-xl italic text-gray-700 dark:text-gray-300 shadow-sm">
          <div className="flex items-start gap-2">
            <span className="text-2xl">💡</span>
            <div>{children}</div>
          </div>
        </blockquote>
      );
    },
    strong({ children }) {
      return <strong className="font-bold text-blue-700 dark:text-blue-400">{children}</strong>;
    },
    em({ children }) {
      return <em className="italic text-gray-600 dark:text-gray-400">{children}</em>;
    },
    hr() {
      return <hr className="my-6 border-0 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent" />;
    }
  };

  return (
    <ReactMarkdown 
      className={`${baseClasses} ${className}`}
      remarkPlugins={[remarkGfm]}
      components={components}
    >
      {children}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
